import React from 'react';
import { Modal, Form, Select, Input, Button, Tabs, Table, Spin } from 'antd';
import { Tags, Trash2 } from 'lucide-react';
import { StatusTag, IconAction, IconActions, EmptyState } from '../../common';
import { useTagAssignmentController } from './tagAssignment.controller';

const { Option } = Select;
const { TextArea } = Input;

const modelLabels = {
    User: 'Usuarios',
    Client: 'Clientes',
    Store: 'Tiendas',
    Product: 'Productos',
    TradeCampaign: 'Campañas',
    TradeContract: 'Contrataciones'
};

function TagAssignmentModal({ visible, tag, onSuccess, onCancel, onMessage }) {
    const {
        form,
        loading,
        items,
        loadingItems,
        taggedItems,
        loadingTagged,
        selectedIds,
        setSelectedIds,
        handleModelTypeChange,
        handleAssign,
        handleRemove,
        getItemName
    } = useTagAssignmentController({ visible, tag, onSuccess, onMessage });

    const taggedColumns = [
        {
            title: 'Tipo',
            dataIndex: 'modelType',
            key: 'modelType',
            render: (type) => <StatusTag tone="neutral">{modelLabels[type] || type}</StatusTag>
        },
        {
            title: 'ID',
            dataIndex: 'modelId',
            key: 'modelId',
            ellipsis: true,
            render: (id) => <span className="font-mono text-xs text-surface-600">{id}</span>
        },
        {
            title: 'Asignado por',
            dataIndex: ['assignedBy', 'name'],
            key: 'assignedBy'
        },
        {
            title: 'Fecha',
            dataIndex: 'assignedAt',
            key: 'assignedAt',
            render: (date) => <span className="tabular-nums">{new Date(date).toLocaleDateString()}</span>
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 80,
            render: (_, record) => (
                <IconActions>
                    <IconAction icon={<Trash2 />} label="Remover" danger onClick={() => handleRemove(record.modelId, record.modelType)} />
                </IconActions>
            )
        }
    ];

    return (
        <Modal
            title={
                <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-[16px] text-primary-600">
                        <Tags />
                    </span>
                    <span className="min-w-0 truncate text-[15px] font-semibold text-surface-900">Asignar Etiqueta: {tag?.name}</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={800}
            destroyOnClose>
            <Tabs
                items={[
                    {
                        key: 'assign',
                        label: 'Asignar',
                        children: (
                            <Form form={form} layout="vertical" onFinish={handleAssign}>
                                <Form.Item name="modelType" label="Tipo de Elemento" initialValue={tag?.applicableModels[0]} rules={[{ required: true }]}>
                                    <Select onChange={handleModelTypeChange} disabled={loadingItems}>
                                        {tag?.applicableModels.map((model) => (
                                            <Option key={model} value={model}>
                                                {modelLabels[model] || model}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                <Form.Item label="Elementos a Etiquetar" extra={`${selectedIds.length} elemento(s) seleccionado(s)`}>
                                    {loadingItems ? (
                                        <div className="flex h-8 items-center">
                                            <Spin size="small" />
                                        </div>
                                    ) : (
                                        <Select
                                            mode="multiple"
                                            placeholder="Selecciona elementos..."
                                            value={selectedIds}
                                            onChange={setSelectedIds}
                                            className="w-full"
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}>
                                            {items.map((item) => (
                                                <Option key={item._id} value={item._id}>
                                                    {getItemName(item)}
                                                </Option>
                                            ))}
                                        </Select>
                                    )}
                                </Form.Item>

                                <Form.Item name="notes" label="Notas (Opcional)">
                                    <TextArea rows={2} placeholder="¿Por qué asignas esta etiqueta?" />
                                </Form.Item>

                                <div className="flex justify-end gap-2 border-t border-surface-100 pt-4">
                                    <Button onClick={onCancel}>Cancelar</Button>
                                    <Button type="primary" htmlType="submit" loading={loading} icon={<Tags />}>
                                        Asignar Etiqueta
                                    </Button>
                                </div>
                            </Form>
                        )
                    },
                    {
                        key: 'current',
                        label: `Elementos Etiquetados (${taggedItems.length})`,
                        children: (
                            <div className="overflow-hidden rounded-lg border border-surface-200">
                                <Table
                                    columns={taggedColumns}
                                    dataSource={taggedItems}
                                    rowKey="_id"
                                    loading={loadingTagged}
                                    pagination={{ pageSize: 10 }}
                                    size="small"
                                    locale={{
                                        emptyText: (
                                            <EmptyState
                                                icon={<Tags />}
                                                title="Sin elementos etiquetados"
                                                description="Asigna esta etiqueta desde la pestaña Asignar"
                                            />
                                        )
                                    }}
                                />
                            </div>
                        )
                    }
                ]}
            />
        </Modal>
    );
}

export default TagAssignmentModal;
